import * as THREE from 'three';
import { toonMat, createToonGradientMap } from './materials.js';

// ── Gold bar currency (phase 21) ─────────────────────────────────────────────
const KEY_GOLD = 'fps_gold_bars_v1';

export const GOLD_PER_ELIM = 1;
export const GOLD_VEND_MED_COST = 2;
export const GOLD_VEND_SHIELD_COST = 3;
export const GOLD_VEND_WEAPON_COST = 5;

export type VendGoldItem = 'med' | 'shield' | 'weapon';

export class FpsGoldBars {
  static load(): number {
    try {
      const raw = localStorage.getItem(KEY_GOLD);
      const n = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(n) && n >= 0 ? n : 0;
    } catch {
      return 0;
    }
  }

  static save(amount: number): void {
    try {
      localStorage.setItem(KEY_GOLD, String(Math.max(0, Math.floor(amount))));
    } catch {
      /* ignore quota / private mode */
    }
  }

  static add(amount: number): number {
    const next = this.load() + Math.max(0, Math.floor(amount));
    this.save(next);
    return next;
  }

  static spend(amount: number): boolean {
    const cost = Math.max(0, Math.floor(amount));
    const cur = this.load();
    if (cur < cost) return false;
    this.save(cur - cost);
    return true;
  }
}

// ── Exported types ────────────────────────────────────────────────────────────
export type VendorKind = 'bounty' | 'upgrade' | 'info' | 'hire' | 'emote' | 'gold_vend';

export interface VendorInteractResult {
  kind: VendorKind;
  mats: number;       // mats spent (negative = gained)
  bountyTarget: string | null;
  goldSpent: number;
  vendItem: VendGoldItem | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const INTERACT_RADIUS = 4;
const INTERACT_RADIUS_SQ = INTERACT_RADIUS * INTERACT_RADIUS;
const COOLDOWN = 30;
const INFO_REVEAL_DURATION = 8;

const BOT_NAMES = [
  'ShadowRift', 'VortexBlade', 'NeonStrike', 'IronClad', 'PhantomX',
  'CrimsonEdge', 'StormWalker', 'GhostUnit', 'DarkPulse', 'BlazeFury',
];

interface VendorDef {
  name: string;
  location: string;
  kind: VendorKind;
  color: number;
  x: number;
  z: number;
  cost: number;       // mats cost; negative = reward
  description: string;
  actionLabel: string;
}

const VENDOR_DEFS: VendorDef[] = [
  {
    name: 'Bounty Board',
    location: 'Tilted Towers',
    kind: 'bounty',
    color: 0xffd700,
    x: 2, z: -38,
    cost: -200,       // gain 200 mats on completion
    description: 'Track down and eliminate a named target for a hefty reward.',
    actionLabel: 'Accept Bounty (+200 mats)',
  },
  {
    name: 'Upgrade Bench',
    location: 'Retail Row',
    kind: 'upgrade',
    color: 0x00e5ff,
    x: -35, z: 22,
    cost: 75,
    description: 'Upgrade your current weapon to the next rarity tier.',
    actionLabel: 'Upgrade Weapon (75 mats)',
  },
  {
    name: 'Info Scout',
    location: 'Pleasant Park',
    kind: 'info',
    color: 0x44ff88,
    x: 40, z: 15,
    cost: 50,
    description: 'Reveals all bot positions on the minimap for 8 seconds.',
    actionLabel: 'Buy Intel (50 mats)',
  },
  {
    name: 'Hire-a-Bot',
    location: 'Lucky Landing',
    kind: 'hire',
    color: 0xaa44ff,
    x: -22, z: -28,
    cost: 100,
    description: 'Hire a friendly escort bot to follow and assist you.',
    actionLabel: 'Hire Escort (100 mats)',
  },
  {
    name: 'Emote Vendor',
    location: 'Cracked Cove',
    kind: 'emote',
    color: 0xff8822,
    x: 28, z: -22,
    cost: 30,
    description: 'Learn a new emote and add it to your collection.',
    actionLabel: 'Buy Emote (30 mats)',
  },
  {
    name: 'Gold Vending',
    location: 'Salty Springs',
    kind: 'gold_vend',
    color: 0xffc400,
    x: -8, z: 42,
    cost: 0,
    description: 'Spend gold bars on heals and weapons. Earn gold from eliminations.',
    actionLabel: 'Buy Med (2 gold)',
  },
];

const GOLD_VEND_OPTIONS: { item: VendGoldItem; cost: number; label: string; hint: string }[] = [
  { item: 'med', cost: GOLD_VEND_MED_COST, label: 'Med Kit', hint: '+25 HP' },
  { item: 'shield', cost: GOLD_VEND_SHIELD_COST, label: 'Shield', hint: '+45 shield' },
  { item: 'weapon', cost: GOLD_VEND_WEAPON_COST, label: 'Weapon', hint: 'random uncommon+' },
];

const EMOTES = ['Floss', 'Orange Justice', 'Take the L', 'Hype', 'Electro Shuffle', 'Robot', 'Worm'];

// ── Internal per-vendor state ─────────────────────────────────────────────────
interface VendorState {
  def: VendorDef;
  group: THREE.Group;
  marker: THREE.Sprite;
  bobPhase: number;
  cooldown: number;   // seconds remaining on cooldown
  panelOpen: boolean;
  backdrop: HTMLDivElement;
  panel: HTMLDivElement;
  bodyEl: HTMLDivElement;
  actionBtn: HTMLButtonElement;
  cycleBtn: HTMLButtonElement | null;
  cooldownEl: HTMLDivElement;
  goldVendIndex: number;
}

// ── Class ─────────────────────────────────────────────────────────────────────
export class FpsNpcVendors {
  private readonly vendors: VendorState[] = [];
  private readonly toonGrad: THREE.DataTexture;

  // Transient state
  private activeBountyTarget: string | null = null;
  private infoRevealTimer = 0;
  private openVendor: VendorState | null = null;
  private bountyPingTimer = 0;
  private readonly bountyHud: HTMLDivElement;
  private readonly onBountyPing?: (x: number, z: number) => void;

  // Escape key listener
  private readonly escListener: (e: KeyboardEvent) => void;

  constructor(
    private readonly scene: THREE.Scene,
    private readonly mount: HTMLElement,
    private readonly getMats: () => number,
    private readonly spendMats: (n: number) => void,
    opts?: { onBountyPing?: (x: number, z: number) => void },
  ) {
    this.onBountyPing = opts?.onBountyPing;
    this.toonGrad = createToonGradientMap();

    for (const def of VENDOR_DEFS) {
      const vendor = this.buildVendor(def);
      this.vendors.push(vendor);
    }

    this.escListener = (e: KeyboardEvent) => {
      if (e.code === 'Escape' && this.openVendor) {
        this.closePanel(this.openVendor);
      }
    };
    window.addEventListener('keydown', this.escListener);

    this.bountyHud = document.createElement('div');
    this.bountyHud.style.cssText = [
      'position:fixed',
      'top:108px',
      'left:50%',
      'transform:translateX(-50%)',
      'z-index:44',
      'display:none',
      'padding:8px 16px',
      'border-radius:10px',
      'background:rgba(28,22,6,0.92)',
      'border:2px solid rgba(255,213,74,0.75)',
      'box-shadow:0 6px 22px rgba(0,0,0,0.45)',
      'color:#ffe9a0',
      'font:800 11px system-ui,sans-serif',
      'letter-spacing:0.06em',
      'pointer-events:none',
      'white-space:nowrap',
    ].join(';');
    this.mount.appendChild(this.bountyHud);
  }

  // ── Build a single vendor NPC ───────────────────────────────────────────────
  private buildVendor(def: VendorDef): VendorState {
    const group = new THREE.Group();
    group.position.set(def.x, 0, def.z);
    group.rotation.y = Math.random() * Math.PI * 2;

    this.buildNpcMesh(group, def.color);

    const marker = this.createMarker(def.color);
    group.add(marker);
    this.scene.add(group);

    // ── DOM panel ────────────────────────────────────────────────────────────
    const backdrop = document.createElement('div');
    backdrop.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:95',
      'display:none', 'align-items:center', 'justify-content:center',
      'background:rgba(6,10,16,0.58)',
      'backdrop-filter:blur(7px)', '-webkit-backdrop-filter:blur(7px)',
      'pointer-events:auto',
    ].join(';');

    const accentHex = '#' + def.color.toString(16).padStart(6, '0');

    const panel = document.createElement('div');
    panel.style.cssText = [
      'min-width:310px', 'padding:20px 22px 16px',
      'border-radius:14px',
      'background:linear-gradient(165deg,rgba(24,28,42,0.97),rgba(10,14,24,0.98))',
      `border:2px solid ${accentHex}99`,
      'box-shadow:0 10px 36px rgba(0,0,0,0.52),inset 0 1px 0 rgba(255,255,255,0.05)',
      'color:#e8f4ff', 'font:14px system-ui,Segoe UI,sans-serif',
      'pointer-events:auto',
    ].join(';');

    const titleEl = document.createElement('div');
    titleEl.style.cssText = `font-weight:700;font-size:16px;letter-spacing:0.05em;margin-bottom:2px;color:${accentHex}`;
    titleEl.textContent = def.name;
    panel.appendChild(titleEl);

    const subEl = document.createElement('div');
    subEl.style.cssText = 'opacity:0.65;font-size:12px;margin-bottom:12px';
    subEl.textContent = def.location;
    panel.appendChild(subEl);

    const bodyEl = document.createElement('div');
    bodyEl.style.cssText = 'margin-bottom:14px;font-size:13px;line-height:1.5;color:#d8e8ff';
    bodyEl.textContent = def.description;
    panel.appendChild(bodyEl);

    const actionBtn = document.createElement('button');
    actionBtn.type = 'button';
    actionBtn.textContent = def.actionLabel;
    actionBtn.style.cssText = [
      'width:100%', 'padding:10px 12px', 'border-radius:10px',
      `border:1px solid ${accentHex}66`,
      'background:rgba(20,18,10,0.9)',
      `color:${accentHex}`,
      'font:700 13px system-ui,sans-serif',
      'cursor:pointer', 'transition:background 0.12s,opacity 0.12s',
    ].join(';');
    panel.appendChild(actionBtn);

    let cycleBtn: HTMLButtonElement | null = null;
    if (def.kind === 'gold_vend') {
      cycleBtn = document.createElement('button');
      cycleBtn.type = 'button';
      cycleBtn.textContent = 'Next item (Q)';
      cycleBtn.style.cssText = [
        'width:100%', 'margin-top:8px', 'padding:8px 12px', 'border-radius:10px',
        `border:1px solid ${accentHex}44`,
        'background:rgba(12,14,22,0.9)',
        `color:${accentHex}`,
        'font:600 12px system-ui,sans-serif',
        'cursor:pointer',
      ].join(';');
      panel.appendChild(cycleBtn);
    }

    const cooldownEl = document.createElement('div');
    cooldownEl.style.cssText = 'margin-top:8px;text-align:center;opacity:0.55;font-size:11px;display:none';
    panel.appendChild(cooldownEl);

    const foot = document.createElement('div');
    foot.textContent = 'Esc — close';
    foot.style.cssText = 'margin-top:10px;text-align:center;opacity:0.45;font-size:11px';
    panel.appendChild(foot);

    backdrop.appendChild(panel);
    this.mount.appendChild(backdrop);

    const state: VendorState = {
      def, group, marker,
      bobPhase: Math.random() * Math.PI * 2,
      cooldown: 0,
      panelOpen: false,
      backdrop, panel, bodyEl, actionBtn, cycleBtn, cooldownEl,
      goldVendIndex: 0,
    };

    actionBtn.addEventListener('click', () => this.handlePurchase(state));
    cycleBtn?.addEventListener('click', () => this.cycleGoldVendOption(state));

    return state;
  }

  private cycleGoldVendOption(v: VendorState): void {
    if (v.def.kind !== 'gold_vend') return;
    v.goldVendIndex = (v.goldVendIndex + 1) % GOLD_VEND_OPTIONS.length;
    this.refreshGoldVendPanel(v);
  }

  private refreshGoldVendPanel(v: VendorState): void {
    const opt = GOLD_VEND_OPTIONS[v.goldVendIndex % GOLD_VEND_OPTIONS.length]!;
    const gold = FpsGoldBars.load();
    v.bodyEl.innerHTML =
      `Balance: <span style="color:#ffd54a">${gold} gold</span><br/>` +
      `${opt.label} — ${opt.cost} gold → ${opt.hint}`;
    v.actionBtn.textContent = `Buy ${opt.label} (${opt.cost} gold)`;
    v.actionBtn.disabled = gold < opt.cost || v.cooldown > 0;
    v.actionBtn.style.opacity = v.actionBtn.disabled ? '0.45' : '1';
  }

  private buildNpcMesh(group: THREE.Group, color: number): void {
    const skinColor = 0xd4a574;
    const skin = toonMat(skinColor, this.toonGrad, { emissive: 0x221408, emissiveIntensity: 0.12 });
    const body = toonMat(color, this.toonGrad, { emissive: 0x080808, emissiveIntensity: 0.15 });
    const pants = toonMat(0x2a3344, this.toonGrad, { emissive: 0x080c14, emissiveIntensity: 0.1 });

    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.52, 0.72, 0.32), body);
    torso.position.y = 1.08;
    torso.castShadow = true;
    group.add(torso);

    const head = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, 0.34), skin);
    head.position.y = 1.62;
    head.castShadow = true;
    group.add(head);

    // Arms
    const armGeo = new THREE.BoxGeometry(0.16, 0.44, 0.16);
    for (const side of [-1, 1] as const) {
      const arm = new THREE.Mesh(armGeo, side === -1 ? body : skin);
      arm.position.set(side * 0.34, 1.0, 0);
      arm.castShadow = true;
      group.add(arm);
    }

    // Legs
    const legGeo = new THREE.BoxGeometry(0.16, 0.52, 0.16);
    for (const side of [-1, 1] as const) {
      const leg = new THREE.Mesh(legGeo, pants);
      leg.position.set(side * 0.14, 0.48, 0);
      leg.castShadow = true;
      group.add(leg);
    }

    // Platform
    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.62, 0.08, 12),
      toonMat(0x5a7080, this.toonGrad, { emissive: 0x182028, emissiveIntensity: 0.1 }),
    );
    platform.position.y = 0.04;
    platform.receiveShadow = true;
    group.add(platform);
  }

  private createMarker(color: number): THREE.Sprite {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;
    const hex = '#' + color.toString(16).padStart(6, '0');
    ctx.fillStyle = hex;
    ctx.beginPath();
    ctx.arc(32, 32, 28, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = '#111';
    ctx.font = 'bold 40px system-ui,sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('!', 32, 34);

    const tex = new THREE.CanvasTexture(canvas);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false });
    const sprite = new THREE.Sprite(mat);
    sprite.scale.set(0.55, 0.55, 1);
    sprite.position.y = 2.15;
    return sprite;
  }

  // ── Purchase handler ────────────────────────────────────────────────────────
  private pendingResult: VendorInteractResult | null = null;

  private handlePurchase(v: VendorState): void {
    if (v.cooldown > 0) return;

    const def = v.def;
    const mats = this.getMats();

    // Bounty gives mats (cost negative) — no payment required
    const cost = def.cost < 0 ? 0 : def.cost;
    let goldSpent = 0;
    let vendItem: VendGoldItem | null = null;

    if (def.kind === 'gold_vend') {
      const opt = GOLD_VEND_OPTIONS[v.goldVendIndex % GOLD_VEND_OPTIONS.length]!;
      if (!FpsGoldBars.spend(opt.cost)) {
        v.bodyEl.innerHTML = `<span style="color:#ff6666">Not enough gold! (need ${opt.cost})</span>`;
        return;
      }
      goldSpent = opt.cost;
      vendItem = opt.item;
      v.bodyEl.innerHTML = `<strong>Purchased!</strong><br/>${opt.label} — ${opt.hint}`;
    } else if (cost > 0 && mats < cost) {
      v.bodyEl.innerHTML = `<span style="color:#ff6666">Not enough mats! (need ${cost})</span>`;
      return;
    }

    // Execute effect
    let bountyTarget: string | null = null;

    switch (def.kind) {
      case 'bounty': {
        bountyTarget = BOT_NAMES[Math.floor(Math.random() * BOT_NAMES.length)];
        this.activeBountyTarget = bountyTarget;
        v.bodyEl.innerHTML = `<strong>Bounty issued!</strong><br/>Target: <span style="color:#ffd700">${bountyTarget}</span><br/>Eliminate them to claim 200 mats.`;
        break;
      }
      case 'upgrade': {
        this.spendMats(cost);
        v.bodyEl.innerHTML = '<strong>Weapon upgraded!</strong><br/>Your current weapon has been promoted one rarity tier.';
        break;
      }
      case 'info': {
        this.spendMats(cost);
        this.infoRevealTimer = INFO_REVEAL_DURATION;
        v.bodyEl.innerHTML = '<strong>Intel purchased!</strong><br/>All bot positions revealed on minimap for 8s.';
        break;
      }
      case 'hire': {
        this.spendMats(cost);
        v.bodyEl.innerHTML = '<strong>Escort hired!</strong><br/>A friendly bot is now following you.';
        break;
      }
      case 'emote': {
        this.spendMats(cost);
        const existing: string[] = JSON.parse(localStorage.getItem('stormsurge_emotes') ?? '[]');
        const available = EMOTES.filter((e) => !existing.includes(e));
        const learned = available.length > 0
          ? available[Math.floor(Math.random() * available.length)]
          : EMOTES[Math.floor(Math.random() * EMOTES.length)];
        if (!existing.includes(learned)) existing.push(learned);
        localStorage.setItem('stormsurge_emotes', JSON.stringify(existing));
        v.bodyEl.innerHTML = `<strong>Emote learned!</strong><br/><span style="color:#ff8822">${learned}</span> added to your collection.`;
        break;
      }
      case 'gold_vend':
        break;
      default: {
        const _exhaustive: never = def.kind;
        void _exhaustive;
        break;
      }
    }

    // Disable button and start cooldown display
    v.actionBtn.disabled = true;
    v.actionBtn.style.opacity = '0.45';
    v.actionBtn.style.cursor = 'default';
    v.cooldown = COOLDOWN;
    v.cooldownEl.style.display = 'block';

    // Stash result to return from update()
    this.pendingResult = { kind: def.kind, mats: def.cost, bountyTarget, goldSpent, vendItem };

    // Auto-close after 1.6s
    setTimeout(() => {
      if (v.panelOpen) this.closePanel(v);
    }, 1600);
  }

  // ── Panel open/close ────────────────────────────────────────────────────────
  private openPanel(v: VendorState): void {
    if (this.openVendor && this.openVendor !== v) this.closePanel(this.openVendor);
    v.panelOpen = true;
    v.backdrop.style.display = 'flex';
    this.openVendor = v;

    // Refresh button state
    const onCooldown = v.cooldown > 0;
    v.actionBtn.disabled = onCooldown;
    v.actionBtn.style.opacity = onCooldown ? '0.45' : '1';
    v.actionBtn.style.cursor = onCooldown ? 'default' : 'pointer';
    if (v.def.kind === 'gold_vend') {
      this.refreshGoldVendPanel(v);
    } else {
      v.bodyEl.textContent = v.def.description;
    }
  }

  private closePanel(v: VendorState): void {
    v.panelOpen = false;
    v.backdrop.style.display = 'none';
    if (this.openVendor === v) this.openVendor = null;
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  private refreshBountyHud(): void {
    if (!this.activeBountyTarget) {
      this.bountyHud.style.display = 'none';
      return;
    }
    this.bountyHud.style.display = 'block';
    this.bountyHud.innerHTML =
      `🎯 BOUNTY: <span style="color:#ffd54a">${this.activeBountyTarget}</span>` +
      ' · +200 mats on elim';
  }

  private tickBountyPing(
    dt: number,
    getTargetPos?: () => { x: number; z: number } | null,
  ): void {
    if (!this.activeBountyTarget || !getTargetPos) return;
    this.bountyPingTimer -= dt;
    if (this.bountyPingTimer > 0) return;
    this.bountyPingTimer = 8;
    const pos = getTargetPos();
    if (!pos) return;
    this.onBountyPing?.(pos.x, pos.z);
  }

  /** Call each frame. Returns a VendorInteractResult the frame a purchase completes. */
  update(
    dt: number,
    playerPos: THREE.Vector3,
    getBountyTargetPos?: () => { x: number; z: number } | null,
  ): VendorInteractResult | null {
    if (!Number.isFinite(dt) || dt <= 0) dt = 0;

    this.refreshBountyHud();
    this.tickBountyPing(dt, getBountyTargetPos);

    if (this.infoRevealTimer > 0) {
      this.infoRevealTimer = Math.max(0, this.infoRevealTimer - dt);
    }

    for (const v of this.vendors) {
      // Bob marker
      v.bobPhase += dt * 2.2;
      v.marker.position.y = 2.15 + Math.sin(v.bobPhase) * 0.08;

      // Cooldown tick
      if (v.cooldown > 0) {
        v.cooldown = Math.max(0, v.cooldown - dt);
        if (v.cooldown <= 0) {
          v.actionBtn.disabled = false;
          v.actionBtn.style.opacity = '1';
          v.actionBtn.style.cursor = 'pointer';
          v.cooldownEl.style.display = 'none';
          v.bodyEl.textContent = v.def.description;
        } else {
          v.cooldownEl.textContent = `Cooldown: ${Math.ceil(v.cooldown)}s`;
        }
      }

      // Proximity check
      const dx = playerPos.x - v.def.x;
      const dz = playerPos.z - v.def.z;
      const distSq = dx * dx + dz * dz;
      const inRange = distSq <= INTERACT_RADIUS_SQ;

      // Hide marker when on cooldown and not in range
      v.marker.visible = v.cooldown <= 0;

      // If player walked away, close panel
      if (!inRange && v.panelOpen) {
        this.closePanel(v);
      }
    }

    const result = this.pendingResult;
    this.pendingResult = null;
    return result;
  }

  /**
   * Call from FPSGame's keydown handler when the player presses [E].
   * Returns true if a vendor panel was toggled.
   */
  tryInteract(playerPos: THREE.Vector3): boolean {
    for (const v of this.vendors) {
      const dx = playerPos.x - v.def.x;
      const dz = playerPos.z - v.def.z;
      if (dx * dx + dz * dz <= INTERACT_RADIUS_SQ) {
        if (v.panelOpen) {
          this.closePanel(v);
        } else {
          this.openPanel(v);
        }
        return true;
      }
    }
    return false;
  }

  /** Returns the name of the currently active bounty target, or null. */
  getActiveBountyTarget(): string | null {
    return this.activeBountyTarget;
  }

  /** True for 8 seconds after an Info Scout purchase. */
  isRevealingBots(): boolean {
    return this.infoRevealTimer > 0;
  }

  /** Call when the bounty target has been eliminated to clear the contract. */
  clearBounty(): void {
    this.activeBountyTarget = null;
    this.bountyPingTimer = 0;
    this.refreshBountyHud();
  }

  /** Returns true if victim matches the active bounty contract. */
  checkBountyElim(victimName: string): boolean {
    if (!this.activeBountyTarget || victimName !== this.activeBountyTarget) return false;
    this.clearBounty();
    return true;
  }

  /** Cycle gold vending item while panel is open (bound to Q in FPSGame). */
  cycleOpenGoldVend(): boolean {
    if (!this.openVendor || this.openVendor.def.kind !== 'gold_vend') return false;
    this.cycleGoldVendOption(this.openVendor);
    return true;
  }

  /** Returns hint string if player is near a vendor. */
  getNearHint(playerPos: THREE.Vector3): string {
    for (const v of this.vendors) {
      const dx = playerPos.x - v.def.x;
      const dz = playerPos.z - v.def.z;
      if (dx * dx + dz * dz <= INTERACT_RADIUS_SQ) {
        return `[E] ${v.def.name}`;
      }
    }
    return '';
  }

  /** Returns true if any vendor panel is currently open. */
  isAnyPanelOpen(): boolean {
    return this.openVendor !== null;
  }

  closeAllPanels(): void {
    if (this.openVendor) this.closePanel(this.openVendor);
  }

  dispose(): void {
    window.removeEventListener('keydown', this.escListener);
    this.bountyHud.remove();
    for (const v of this.vendors) {
      v.backdrop.remove();
      v.group.removeFromParent();
      v.group.traverse((o) => {
        if (o instanceof THREE.Mesh) {
          o.geometry?.dispose();
          if (o.material instanceof THREE.Material) o.material.dispose();
        }
      });
      const markerMat = v.marker.material as THREE.SpriteMaterial;
      markerMat.map?.dispose();
      markerMat.dispose();
    }
    this.toonGrad.dispose();
  }
}
